import classNames from 'classnames';
import React, { ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../shared/components/button/button';
import Input from '../../shared/components/form-controls/input/input';
import { Option } from '../../shared/components/form-controls/options-modal/options-modal';
import AccountMenu from '../account/account-menu/account-menu';
import { useHubNetworkState } from '../account/hub-network-state-context';
import { useAsset } from '../asset/asset-context';
import { Asset } from '../asset/asset-types';
import { getCurrencyLogoPath, getMainCurrency } from '../currency/currency-service';
import DisplayNameWithIro from '../currency/display-name-with-iro/display-name-with-iro';
import { EARNING_TYPE_DESCRIPTION_MAP, EARNING_TYPES, EarningType } from '../earnings/earnings-types';
import { getNetworkLogoPath } from '../network/network-service';
import { Network } from '../network/network-types';
import QuickAuthAction from '../quick-auth/quick-auth-action/quick-auth-action';
import RollappStatusIndicator from '../rollapp/rollapp-status/indicator/rollapp-status-indicator';
import { ReactComponent as DymensionLogo } from '../../assets/logos/dymension-logo.svg';
import { ReactComponent as AddLiquidityIcon } from '../../assets/icons/add-position.svg';
import { ReactComponent as BridgeIcon } from '../../assets/icons/bridge.svg';
import { ReactComponent as HistoryIcon } from '../../assets/icons/history.svg';
import { ReactComponent as DepositIcon } from '../../assets/icons/download.svg';
import { ReactComponent as WithdrawIcon } from '../../assets/icons/upload.svg';
import { ReactComponent as SwapIcon } from '../../assets/icons/swap-horiz.svg';
import { ReactComponent as PoolsIcon } from '../../assets/icons/pool.svg';
import { ReactComponent as TokenIcon } from '../../assets/icons/token.svg';
import { ReactComponent as DashboardIcon } from '../../assets/icons/monitoring.svg';
import { ReactComponent as StakeIcon } from '../../assets/icons/validator.svg';
import { ReactComponent as VoteIcon } from '../../assets/icons/vote.svg';
import { ReactComponent as EndorseIcon } from '../../assets/icons/endorsement.svg';
import { ReactComponent as DocsIcon } from '../../assets/icons/docs.svg';
import { ReactComponent as ExplorerIcon } from '../../assets/icons/explorer.svg';
import { ReactComponent as BlogIcon } from '../../assets/icons/blog.svg';
import { ReactComponent as ForumIcon } from '../../assets/icons/forum.svg';
import { ReactComponent as AddPositionIcon } from '../../assets/icons/add-position.svg';
import { ReactComponent as ValidatorIcon } from '../../assets/icons/validator.svg';
import { ReactComponent as AddDelegationIcon } from '../../assets/icons/add-delegation.svg';
import { ReactComponent as PriceTagIcon } from '../../assets/icons/price-tag.svg';
import { ReactComponent as RoyaltyIcon } from '../../assets/icons/royalty.svg';
import { ReactComponent as UsdcLogo } from '../../assets/logos/usdc-logo.svg';
import { ReactComponent as MoneyBagIcon } from '../../assets/icons/money-bag.svg';
import NavBar, { NavBarItem } from '../../shared/components/nav-bar/nav-bar';
import Icon from '../../shared/components/icon/icon';
import { useNetwork } from '../network/network-context';
import './top-bar.scss';

interface TopBarProps {
    className?: string;
}

const TopBar: React.FC<TopBarProps> = ({ className }) => {
    const navigate = useNavigate();
    const { rollapps, hubNetwork, getNetwork } = useNetwork();
    const { assets, assetMap } = useAsset();
    const { getAssetLink } = useAsset();
    const hubNetworkState = useHubNetworkState();
    const [ accountMenuTriggerWidth, setAccountMenuTriggerWidth ] = useState(0);

    useEffect(() => {
        if (!hubNetworkState.address && !hubNetworkState.addressLoading) {
            setAccountMenuTriggerWidth(120);
        }
    }, [ hubNetworkState.address, hubNetworkState.addressLoading ]);

    const searchFilterPredicate = useCallback((searchText: string, value: string | number): boolean => {
        const searchRegExp = new RegExp(searchText, 'i');
        const asset = assetMap?.[value];
        let { network, currency } = asset || {};
        if (!asset) {
            network = getNetwork(value.toString());
            currency = network && getMainCurrency(network);
        }
        if (!network || !currency) {
            return false;
        }
        return Boolean(
            searchRegExp.test(network.chainName) || searchRegExp.test(network.chainId) || searchRegExp.test(currency.displayDenom));
    }, [ assetMap, getNetwork ]);

    const sortedAssets = useMemo(() => {
        return assets?.sort((asset1, asset2) => (asset2.liquidity || 0) - (asset1.liquidity || 0));
    }, [ assets ]);

    const accountMenuNetworks = useMemo(() => !hubNetwork ? [] : [
        hubNetwork.chainId,
        ...rollapps.filter((rollapp) => rollapp.status === 'Active' || rollapp.status === 'Degraded')
            .map((network) => network.chainId),
    ], [ hubNetwork, rollapps ]);

    const onSearchSuggestionClick = useCallback((key: string) => {
        const asset = assetMap?.[key];
        if (asset) {
            navigate(getAssetLink(asset));
        } else {
            navigate(`/rollapps/${key}/dashboard`);
        }
    }, [ assetMap, getAssetLink, navigate ]);

    const renderNetworkOption = (network?: Network): ReactNode => {
        if (!network) {
            return undefined;
        }
        const currency = getMainCurrency(network);
        return <>
            <img className='network-logo' src={getNetworkLogoPath(network)} alt='network-logo' />
            <span className='network-name'>{network.chainName}</span>
            <span className='main-currency-container'>
                {currency?.displayDenom}
                <RollappStatusIndicator containerClassName='status-indicator-container' status={network.status} />
            </span>
            <span className='space' />
        </>;
    };

    const renderAssetOption = (asset?: Asset): ReactNode => {
        if (!asset) {
            return undefined;
        }
        return <>
            <img className='currency-logo' src={getCurrencyLogoPath(asset.currency, asset.network)} alt='currency-logo' />
            <div className='asset-name'><DisplayNameWithIro coins={asset} /></div>
            <span className='asset-network-name'>{asset.network.chainName}</span>
        </>;
    };

    const getEarningTypeIcon = (type: EarningType): ReactElement => {
        switch (type) {
            case 'Staking':
                return <AddDelegationIcon />;
            case 'Bridge LP':
                return <PriceTagIcon />;
            case 'Endorsement':
                return <EndorseIcon />;
            case 'Pool LP':
                return <AddPositionIcon />;
            case 'Validator':
                return <ValidatorIcon />;
        }
        return <RoyaltyIcon />;
    };

    return (
        <div className={classNames('top-bar', className)}>
            <a href='/' className='logo'>
                <Icon><DymensionLogo /></Icon>
            </a>
            <NavBar className='nav-bar'>
                <NavBarItem label={<>Roll<b><i>Apps</i></b></>} url='/rollapps' />
                <NavBarItem label='DYM' url='/dymension'>
                    <NavBarItem
                        label={<><Icon className='nav-item-icon' color='secondary'><DashboardIcon /></Icon>Dashboard</>}
                        url='/dashboard'
                    />
                    <NavBarItem label={<><Icon className='nav-item-icon' color='secondary'><SwapIcon /></Icon>Trade</>} url='/token' />
                    <NavBarItem label={<><Icon className='nav-item-icon' color='secondary'><StakeIcon /></Icon>Stake</>} url='/staking' />
                    <NavBarItem label={<><Icon className='nav-item-icon' color='secondary'><VoteIcon /></Icon>Vote</>} url='/governance' />
                    <NavBarItem
                        label={<><Icon className='nav-item-icon' color='secondary'><EndorseIcon /></Icon>Endorse</>}
                        url='/endorsement'
                    />
                </NavBarItem>
                <NavBarItem label='Bridge' url='/bridge'>
                    <NavBarItem
                        label={<><Icon className='nav-item-icon' color='secondary'><BridgeIcon /></Icon>Bridge</>}
                        url='/transfer'
                    />
                    <NavBarItem
                        label={<><Icon className='nav-item-icon' color='secondary'><HistoryIcon /></Icon>History</>}
                        url='/status'
                    />
                    <NavBarItem
                        label={<><Icon className='nav-item-icon' color='secondary'><AddLiquidityIcon /></Icon>Provide Liquidity</>}
                        url='/eibc-client'
                    />
                    <NavBarItem
                        label={<><Icon className='nav-item-icon' color='secondary'><DepositIcon /></Icon>Deposit</>}
                        group='USDC Bridge'
                        url='/cctp/deposit'
                    />
                    <NavBarItem
                        label={<><Icon className='nav-item-icon' color='secondary'><WithdrawIcon /></Icon>Withdraw</>}
                        group='USDC Bridge'
                        url='/cctp/withdraw'
                    />
                    <NavBarItem
                        label={<><Icon className='nav-item-icon' color='secondary'><HistoryIcon /></Icon>USDC History</>}
                        group='USDC Bridge'
                        url='/cctp/history'
                    />
                </NavBarItem>
                <NavBarItem label='Trade' url='/amm'>
                    <NavBarItem label={<><Icon className='nav-item-icon' color='secondary'><SwapIcon /></Icon>Swap</>} url='/swap' />
                    <NavBarItem label={<><Icon className='nav-item-icon' color='secondary'><PoolsIcon /></Icon>Pools</>} url='/pools' />
                    <NavBarItem label={<><Icon className='nav-item-icon' color='secondary'><TokenIcon /></Icon>Assets</>} url='/assets' />
                </NavBarItem>
                <NavBarItem label='Earn'>
                    <NavBarItem
                        label={<><Icon className='nav-item-icon' color='secondary'><MoneyBagIcon /></Icon>My Earnings</>}
                        url='/earnings'
                        trackEvent='nav_bar_my-earnings_click'
                    />
                    {EARNING_TYPES.map((type) => (
                        <NavBarItem
                            key={type}
                            group='Ways to Earn'
                            external={EARNING_TYPE_DESCRIPTION_MAP[type].url.startsWith('https://')}
                            label={<><Icon className='nav-item-icon' color='secondary'>{getEarningTypeIcon(type)}</Icon>{type}</>}
                            url={EARNING_TYPE_DESCRIPTION_MAP[type].url}
                        />
                    ))}
                </NavBarItem>
                <NavBarItem label='More'>
                    <NavBarItem
                        label={<><Icon className='nav-item-icon' color='secondary'><DocsIcon /></Icon>Documentation</>}
                        url='https://docs.dymension.xyz'
                        external
                        trackEvent='nav_bar_documentation_click'
                    />
                    <NavBarItem
                        label={<><Icon className='nav-item-icon' color='secondary'><ForumIcon /></Icon>Forum</>}
                        url='https://forum.dymension.xyz'
                        external
                        trackEvent='nav_bar_forum_click'
                    />
                    <NavBarItem
                        label={<><Icon className='nav-item-icon' color='secondary'><ExplorerIcon /></Icon>Explorer</>}
                        url={hubNetwork?.explorerUrl || ''}
                        disabled={!hubNetwork?.explorerUrl}
                        external
                        trackEvent='nav_bar_explorer_click'
                    />
                    <NavBarItem
                        label={<><Icon className='nav-item-icon' color='secondary'><BlogIcon /></Icon>Blog</>}
                        url='https://blog.dymension.xyz'
                        external
                        trackEvent='nav_bar_blog_click'
                    />
                </NavBarItem>
            </NavBar>

            <span className='space top-bar-middle-space' />

            <Input
                type='search'
                placeholder='Search...'
                className='rollapps-search'
                controlSize='small'
                searchFilterPredicate={searchFilterPredicate}
                emptySearchResultsLabel='No RollApps'
                onSuggestionSelect={(key) => onSearchSuggestionClick(key as string)}
            >
                {rollapps.map((rollapp) => (
                    <Option
                        value={rollapp.chainId}
                        key={rollapp.chainId}
                        disabled={rollapp.disabled}
                        group='RollApps'
                        trackEvent='nav_bar_searched_network_click'
                    >
                        {renderNetworkOption(rollapp)}
                    </Option>
                ))}
                {sortedAssets?.map((asset) => (
                    <Option value={asset.key} key={asset.key} group='Assets' trackEvent='nav_bar_searched_asset_click'>
                        {renderAssetOption(asset)}
                    </Option>
                ))}
            </Input>

            <span className='space actions-space' />

            <div className='horizontally-centered actions-container'>
                <Button
                    size='small'
                    className='main-action'
                    buttonType='primary'
                    iconColorMode='original'
                    trackEvent='main_deposit_click'
                    onClick={() => navigate('/bridge/cctp/deposit')}
                >
                    <UsdcLogo /><span className='main-action-text'>Deposit</span>
                </Button>

                <span className='space' />

                <AccountMenu
                    menuAlign='right'
                    triggerSize='small'
                    onTriggerLayout={(trigger) => setAccountMenuTriggerWidth(trigger.clientWidth)}
                    optionalNetworks={accountMenuNetworks}
                />
            </div>

            {accountMenuTriggerWidth ? <QuickAuthAction accountMenuTriggerWidth={accountMenuTriggerWidth} /> : undefined}
        </div>
    );
};

export default TopBar;
